import { get } from "idb-keyval";
import { authorization } from "module/auth";
import { getTeamFonts, saveTeamFonts } from "graphQL/mutations/userFonts";
import { getDesignClientIdGQL } from "pages/creative/pageV2/useDesignManagement";
import { FontCDNData, s3CustomFontService } from "../../services/s3CustomFontService";
import { EmbeddedFont, ShapePlan, ShapeSpec } from "../../utils/svgImport";

const EXTENSIONS: Readonly<Record<string, string>> = {
  "font/ttf": "ttf",
  "font/otf": "otf",
  "font/woff": "woff",
  "font/woff2": "woff2",
};

const dataUrlToFile = (font: EmbeddedFont): File => {
  const binary = atob(font.dataUrl.slice(font.dataUrl.indexOf(",") + 1));
  const bytes = Uint8Array.from(binary, (ch) => ch.charCodeAt(0));
  return new File([bytes], `${font.name}.${EXTENSIONS[font.mimeType] ?? "ttf"}`, { type: font.mimeType });
};

// Google/static fonts the builder already serves; an embedded copy of one of those is not installed.
const builderFontNames = async (): Promise<Set<string>> => {
  try {
    const names = (await get<string[]>("fonts")) ?? [];
    return new Set(names.map((n) => n.toLowerCase()));
  } catch {
    return new Set();
  }
};

/**
 * Admins act outside any team, so their custom-font sync has nowhere to land: an admin import saves
 * the fonts to the team that owns the design instead. Client users return undefined (their active
 * team is already the target). A failed lookup also returns undefined: fonts then stay local.
 */
export const resolveFontTeam = async (designId?: string): Promise<string | undefined> => {
  if (!designId || !authorization.isAdmin()) return undefined;
  try {
    return (await getDesignClientIdGQL(designId)) ?? undefined;
  } catch (error) {
    console.error("SVG import: could not find the design's team, fonts stay local", error);
    return undefined;
  }
};

const teamFontNames = async (teamId: string): Promise<Set<string>> => {
  const result = await (await (getTeamFonts(teamId) as Promise<Response>)).json();
  if (result.errors?.length) throw new Error(result.errors[0].message);
  const fonts: { name: string }[] = result.data?.getUserFonts ?? [];
  return new Set(fonts.map((f) => f.name.toLowerCase()));
};

const saveToTeam = async (font: FontCDNData, teamId: string): Promise<void> => {
  const fontData = {
    name: font.name,
    s3Url: font.s3Url,
    mimeType: font.mimeType,
    uploadedAt: String(font.uploadedAt),
    fileSize: String(font.fileSize),
  };
  const result = await (await (saveTeamFonts([fontData], teamId) as Promise<Response>)).json();
  const saved = result.data?.saveUserFonts;
  if (result.errors?.length || !saved?.success) {
    throw new Error(result.errors?.[0]?.message ?? saved?.error ?? "saveUserFonts failed");
  }
};

interface InstallContext {
  readonly registry: ReadonlyMap<string, FontCDNData>;
  readonly team?: { readonly id: string; readonly fonts: ReadonlySet<string> };
}

const installOne = async (font: EmbeddedFont, { registry, team }: InstallContext): Promise<void> => {
  const face = new FontFace(font.name, `url(${font.dataUrl})`);
  await face.load();
  document.fonts.add(face);
  const key = font.name.toLowerCase();
  if (team) {
    if (team.fonts.has(key)) return; // the team already has this font
    const uploaded = registry.get(key) ?? (await s3CustomFontService.uploadFontToCDN(dataUrlToFile(font), font.name));
    await saveToTeam(uploaded, team.id);
    return;
  }
  // Same name already in the user's fonts: reuse it rather than uploading a duplicate.
  if (registry.has(key)) return;
  await s3CustomFontService.uploadFontToCDN(dataUrlToFile(font), font.name);
};

// An unreadable team keeps the fonts local (as before admin imports could target a team).
const loadTeam = async (teamId?: string): Promise<InstallContext["team"]> => {
  if (!teamId) return undefined;
  try {
    return { id: teamId, fonts: await teamFontNames(teamId) };
  } catch (error) {
    console.error(`SVG import: could not read the fonts of team ${teamId}, keeping them local`, error);
    return undefined;
  }
};

/**
 * Installs the SVG's embedded fonts as custom fonts (S3 + font registry, as the font upload does),
 * saved to `teamId` when given (an admin importing into a client's design), and returns the names
 * that are usable. Failures fall back to the SVG's family.
 */
export const installEmbeddedFonts = async (fonts: readonly EmbeddedFont[], teamId?: string): Promise<Set<string>> => {
  const installed = new Set<string>();
  if (fonts.length === 0 || typeof FontFace === "undefined") return installed;
  const [registry, team, builderFonts] = await Promise.all([
    s3CustomFontService.getLocalFontRegistry().catch(() => [] as FontCDNData[]),
    loadTeam(teamId),
    builderFontNames(),
  ]);
  const context: InstallContext = { registry: new Map(registry.map((f) => [f.name.toLowerCase(), f])), team };
  // One at a time: the font service read-modify-writes its registry, so parallel uploads lose entries.
  const candidates = fonts.filter((font) => !builderFonts.has(font.family.toLowerCase()));
  for (const font of candidates) {
    try {
      // eslint-disable-next-line no-await-in-loop
      await installOne(font, context);
      installed.add(font.name);
    } catch (error) {
      console.error(`SVG import: could not install embedded font "${font.name}"`, error);
    }
  }
  if (installed.size > 0) window.dispatchEvent(new Event("custom-fonts-updated"));
  return installed;
};

/** Points text whose embedded font was not installed back at the SVG's own family and weight. */
export const applyFontFallbacks = (plan: ShapePlan, installed: ReadonlySet<string>): ShapePlan => {
  const fallbacks = new Map(
    plan.fontFallbacks.filter((f) => !installed.has(f.fontName)).map((f) => [f.id, f] as const)
  );
  if (fallbacks.size === 0) return plan;
  const shapes = plan.shapes.map((shape): ShapeSpec => {
    const fallback = fallbacks.get(shape.id);
    if (!fallback) return shape;
    const { font, fontWeight, italic } = fallback;
    return { ...shape, props: { ...shape.props, font, fontWeight, italic } };
  });
  return { ...plan, shapes };
};
